import {
  CardLoading,
  CardNotice,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiPalette, BiVizType } from '@euler/model/bi/viz';
import { memo } from 'react';
import { TextInput, View } from 'react-native';
import { BiRetry } from '../../components/ui/BiRetry';
import { EditorPreview } from './EditorPreview';
import { QuerySections } from './QuerySections';
import { VizSection } from './VizSection';
import { BlendSection } from './blend/BlendSection';
import { FormatSection } from './format/FormatSection';
import { biEditorWithDataset } from './functions/biEditorState';
import { BiCardEditor } from './functions/useBiCardEditor';
import { SeriesSection } from './series/SeriesSection';
import { StyleSection } from './style/StyleSection';
import { TotalsSection } from './totals/TotalsSection';

const kMaxTitle = 100;

/**
 * The card form's fields: title, dataset, measures, dimensions and grain,
 * time range, filters, sort, limit, a second dataset to combine with,
 * chart type, series, number formats and style — and, unless the host
 * draws it, the live preview.
 */
export const CardEditorForm = memo(
  ({
    editor,
    palette,
    withPreview,
  }: {
    editor: BiCardEditor;
    /** The dashboard's palette, which the card takes unless it sets one. */
    palette: BiPalette;
    /** The card page shows the preview in its own column instead. */
    withPreview: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const { state, setState, catalogs, catalog, problem, viz, preview } =
      editor;
    const result = preview.kind === 'ready' ? preview.result : null;
    const ready = catalog.state.kind === 'ready' ? catalog.state.catalog : null;
    return (
      <View style={{ gap: rpx(18) }}>
        <TextInput
          accessibilityLabel="图表标题"
          value={state.title}
          onChangeText={text =>
            setState(current => ({ ...current, title: text }))
          }
          maxLength={kMaxTitle}
          placeholder="图表标题，例如：上月各服务类型营收"
          placeholderTextColor={aiColors.mutedForeground}
          style={{
            height: rpx(40),
            paddingHorizontal: rpx(12),
            borderRadius: rpx(10),
            borderWidth: 1,
            borderColor: aiColors.border,
            backgroundColor: aiColors.input,
            color: aiColors.foreground,
            fontSize: rpx(14),
          }}
        />
        {withPreview ? (
          <EditorPreview
            problem={problem}
            preview={preview}
            viz={viz}
            palette={palette}
          />
        ) : null}
        {catalogs.state.kind === 'loading' ? (
          <CardLoading label="正在读取数据集…" />
        ) : catalogs.state.kind === 'failed' ? (
          <View style={{ gap: rpx(8) }}>
            <CardNotice
              tone="danger"
              text="数据集目录暂时读不到，重新读取后才能保存。"
            />
            <BiRetry label="重新读取" onRetry={catalogs.retry} />
          </View>
        ) : (
          <>
            <QuerySections
              query={state}
              catalog={catalog.state}
              builtin={catalogs.state.builtin}
              uploaded={catalogs.state.uploaded}
              datasetTitle={state.blend ? '第一份数据' : undefined}
              exclude={state.blend?.second.dataset ?? null}
              withDimensions={!state.blend}
              withSortLimit={!state.totalsParts.length}
              onPick={dataset => setState(biEditorWithDataset(state, dataset))}
              onChange={query =>
                setState({
                  ...state,
                  ...query,
                  kpiCompare: query.timeRange ? state.kpiCompare : false,
                })
              }
              onRetryUploads={catalogs.retry}
              onRetryCatalog={catalog.retry}
            />
            {state.vizType === BiVizType.KpiGroup ? (
              <TotalsSection
                state={state}
                builtin={catalogs.state.builtin}
                uploaded={catalogs.state.uploaded}
                onChange={setState}
                onRetryUploads={catalogs.retry}
              />
            ) : (
              <BlendSection
                state={state}
                first={ready}
                builtin={catalogs.state.builtin}
                uploaded={catalogs.state.uploaded}
                onChange={setState}
                onRetryUploads={catalogs.retry}
              />
            )}
          </>
        )}
        <VizSection state={state} result={result} onChange={setState} />
        <SeriesSection
          viz={viz}
          looks={state.seriesLooks}
          result={result}
          onChange={seriesLooks =>
            setState(current => ({ ...current, seriesLooks }))
          }
        />
        <FormatSection
          state={state}
          viz={viz}
          result={result}
          onChange={setState}
        />
        <StyleSection
          viz={viz}
          style={state.style}
          result={result}
          dashboardPalette={palette}
          onChange={style => setState(current => ({ ...current, style }))}
        />
      </View>
    );
  },
);
